import React, { useId } from "react";

/**
 * Animated Verasist logo — SVG sound-wave bars with glow effects.
 * Inspired by the /automation page's VerasistLogoAnimated.
 */
export default function AnimatedVerasistLogo({
  size = 120,
  animated = true,
  className = "",
}) {
  const uid = useId().replace(/:/g, "");
  const colorLight = "#60a5fa";
  const colorDark = "#3b82f6";

  const bars = [
    {
      x: 118,
      y: 793,
      width: 60,
      height: 266,
      midY: 926,
      dur: 1.3,
      delay: 0.1,
      kf: "0%{transform:scaleY(.6)}25%{transform:scaleY(1.05)}50%{transform:scaleY(.5)}75%{transform:scaleY(.95)}100%{transform:scaleY(.6)}",
    },
    {
      x: 344.5,
      y: 727,
      width: 60,
      height: 584,
      midY: 1019,
      dur: 1.6,
      delay: 0.3,
      kf: "0%{transform:scaleY(.9)}20%{transform:scaleY(.5)}45%{transform:scaleY(1.1)}70%{transform:scaleY(.6)}100%{transform:scaleY(.9)}",
    },
    {
      x: 580,
      y: 512,
      width: 60,
      height: 981,
      midY: 1002.5,
      dur: 1.1,
      delay: 0,
      kf: "0%{transform:scaleY(1)}30%{transform:scaleY(.55)}55%{transform:scaleY(1.1)}80%{transform:scaleY(.7)}100%{transform:scaleY(1)}",
    },
    {
      x: 802,
      y: 729,
      width: 60,
      height: 585,
      midY: 1021.5,
      dur: 1.5,
      delay: 0.45,
      kf: "0%{transform:scaleY(.6)}25%{transform:scaleY(1.1)}55%{transform:scaleY(.5)}80%{transform:scaleY(.95)}100%{transform:scaleY(.6)}",
    },
    {
      x: 1029.5,
      y: 829,
      width: 60,
      height: 276,
      midY: 967,
      dur: 1.4,
      delay: 0.6,
      kf: "0%{transform:scaleY(.95)}30%{transform:scaleY(.5)}60%{transform:scaleY(1.05)}85%{transform:scaleY(.6)}100%{transform:scaleY(.95)}",
    },
  ];

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size * (1554 / 1210),
        display: "inline-block",
        overflow: "visible",
      }}
    >
      <style>{`
        .vl-outer-${uid} {
          animation: vl-glow-${uid} 2.5s ease-in-out infinite;
        }
        @keyframes vl-glow-${uid} {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 1; }
        }
        ${bars
          .map(
            (bar, i) => `
        .vl-bar-${uid}-${i} {
          animation-name: vl-pulse-${uid}-${i};
          animation-iteration-count: infinite;
          animation-timing-function: ease-in-out;
        }
        @keyframes vl-pulse-${uid}-${i} { ${bar.kf} }`
          )
          .join("\n")}
        @media (prefers-reduced-motion: reduce) {
          .vl-outer-${uid},
          ${bars.map((_, i) => `.vl-bar-${uid}-${i}`).join(", ")} {
            animation: none;
          }
        }
      `}</style>

      <svg
        viewBox="0 0 1210 1554"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        style={{ overflow: "visible" }}
      >
        <defs>
          <filter id={`vl-g-${uid}`} x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="core" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="20" result="mid" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="40" result="outer" />
            <feMerge>
              <feMergeNode in="outer" />
              <feMergeNode in="mid" />
              <feMergeNode in="core" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id={`vl-og-${uid}`} x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="55" result="big" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="90" result="huge" />
            <feMerge>
              <feMergeNode in="huge" />
              <feMergeNode in="big" />
            </feMerge>
          </filter>
        </defs>

        {/* Ambient outer glow */}
        <g
          className={`vl-outer-${uid}`}
          filter={`url(#vl-og-${uid})`}
          opacity="0.5"
        >
          <path
            d="M 7.3 712.3 A 600 600 0 0 1 552.7 62.3 L 560.4 149.9 A 512 512 0 0 0 94.9 704.6 Z"
            fill={colorLight}
          />
          <path
            d="M 657.3 62.3 A 600 600 0 0 1 1202.7 712.3 L 1115.1 704.6 A 512 512 0 0 0 649.6 149.9 Z"
            fill={colorDark}
          />
          {bars.map((bar, i) => (
            <rect key={i} x={bar.x} y={bar.y} width={bar.width} height={bar.height} fill={i % 2 === 0 ? colorLight : colorDark} />
          ))}
        </g>

        {/* Sharp logo with core glow */}
        <g filter={`url(#vl-g-${uid})`}>
          <path
            d="M 7.3 712.3 A 600 600 0 0 1 552.7 62.3 L 560.4 149.9 A 512 512 0 0 0 94.9 704.6 Z"
            fill={colorLight}
          />
          <path
            d="M 657.3 62.3 A 600 600 0 0 1 1202.7 712.3 L 1115.1 704.6 A 512 512 0 0 0 649.6 149.9 Z"
            fill={colorDark}
          />
          {bars.map((bar, i) => (
            <g
              key={i}
              className={animated ? `vl-bar-${uid}-${i}` : undefined}
              style={{
                transformOrigin: `610px ${bar.midY}px`,
                animationDuration: `${bar.dur}s`,
                animationDelay: `${bar.delay}s`,
              }}
            >
              <rect x={bar.x} y={bar.y} width={bar.width} height={bar.height} fill={colorLight} />
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
